import React from 'react';
import { TIER_ART, crestNumeralStyle } from '../lib/rankCrest';

/**
 * Phase 5.0B FINAL — Main match top HUD.
 *
 * Uses `hud_top_shell.svg` as the visual frame (via background-image)
 * with real React content on top. Every prop / testid is the same
 * pass-through contract callers already use — no new state, no new
 * derived timer, no new API.
 *
 * Rank crest mapping is duplicated verbatim from HomeScreen so Home
 * has zero diff and the two screens stay visually aligned.
 */

// --- Home-parity rank crest map (kept in sync with HomeScreen) ------
const DIVISION_ROMAN = { 1: 'I', 2: 'II', 3: 'III' };

function HudCrest({ tier, division, testId }) {
  const key = tier ? String(tier).toLowerCase() : null;
  const src = key && TIER_ART[key] ? TIER_ART[key] : null;
  if (!src) {
    // No valid crest available — render nothing (per correction pass).
    return null;
  }
  const numeral = key !== 'legend' && key !== 'unranked' && division
    ? DIVISION_ROMAN[Number(division)]
    : null;
  return (
    <div className="kk-match-hud__crest-slot" data-testid={testId}>
      <img src={src} alt="" aria-hidden className="kk-match-hud__crest" />
      {numeral && (
        <span className="kk-match-hud__crest-numeral" aria-hidden style={crestNumeralStyle(key)}>{numeral}</span>
      )}
    </div>
  );
}

export default function Scoreboard({
  // Names
  leftName,
  rightName,
  leftNameTestId,
  rightNameTestId,
  leftMeta,
  rightMeta,

  // NEW (optional) — tier / SR / division for the crest + subline.
  // Callers may omit these; the HUD gracefully falls back to `leftMeta`
  // / `rightMeta` strings for the tier line.
  leftTier,
  leftDivision,
  leftSR,
  leftTierLabel,
  rightTier,
  rightDivision,
  rightSR,
  rightTierLabel,

  // Scores
  leftScore,
  rightScore,
  leftScoreTestId = 'score-p1',
  rightScoreTestId = 'score-p2',

  // Active side
  activeSide = null,

  // Info strip
  halfLabel,
  halfTestId = 'half-label',
  deckCount,
  deckTotal,
  deckTestId = 'deck-count',

  // Timer
  timerLabel,
  timerTone = 'default',
  timerProgress, // 0-100; optional — drives the yellow bar
  timerWrapperTestId,
  timerTestId = 'turn-timer',

  // Strikes (online)
  strikesLabel,
  strikesTestId = 'strike-counter',
}) {
  const leftActive  = activeSide === 'left';
  const rightActive = activeSide === 'right';
  // Meta-row turn label — clean short tag, never echoes the raw name
  // (which may already carry a "YOU · " prefix from OnlineGameScreen).
  // Left is always the local player in both GameScreen and OnlineGameScreen.
  const turnOwner = leftActive ? 'YOUR TURN' : rightActive ? 'OPP TURN' : '';
  const leftSubline  = leftTierLabel
    || (leftTier ? `${String(leftTier).toUpperCase()}${DIVISION_ROMAN[Number(leftDivision)] ? ` ${DIVISION_ROMAN[Number(leftDivision)]}` : ''}${leftSR != null ? ` · ${leftSR} SR` : ''}` : leftMeta || '');
  const rightSubline = rightTierLabel
    || (rightTier ? `${String(rightTier).toUpperCase()}${DIVISION_ROMAN[Number(rightDivision)] ? ` ${DIVISION_ROMAN[Number(rightDivision)]}` : ''}${rightSR != null ? ` · ${rightSR} SR` : ''}` : rightMeta || '');
  const clampedProgress = typeof timerProgress === 'number'
    ? Math.max(0, Math.min(100, timerProgress))
    : null;

  return (
    <header
      data-testid="scoreboard"
      className="kk-match-hud"
      style={{
        margin: '4px 8px 0',
        backgroundImage: 'url(/gameplay/hud_top_shell.svg)',
      }}
    >
      {/* Row 1 — crest + name + subline · SCORE · subline + name + crest */}
      <div className="kk-match-hud__top">
        <div className="kk-match-hud__player kk-match-hud__player--left">
          <HudCrest tier={leftTier} division={leftDivision} testId="hud-left-crest" />
          <div className="kk-match-hud__player__id">
            <span
              className="kk-match-hud__name"
              data-testid={leftNameTestId || undefined}
              style={{ color: leftActive ? '#ffffff' : 'rgba(245,243,236,0.75)' }}
            >
              {leftName || 'P1'}
            </span>
            {leftSubline && (
              <span className="kk-match-hud__tier">{leftSubline}</span>
            )}
          </div>
        </div>

        <div
          className="kk-match-hud__score"
          aria-label={`${leftScore ?? 0} to ${rightScore ?? 0}`}
        >
          <span data-testid={leftScoreTestId}>{leftScore ?? 0}</span>
          <i>–</i>
          <span data-testid={rightScoreTestId}>{rightScore ?? 0}</span>
        </div>

        <div className="kk-match-hud__player kk-match-hud__player--right">
          <div className="kk-match-hud__player__id">
            <span
              className="kk-match-hud__name"
              data-testid={rightNameTestId || undefined}
              style={{ color: rightActive ? '#ffffff' : 'rgba(245,243,236,0.75)' }}
            >
              {rightName || 'P2'}
            </span>
            {rightSubline && (
              <span className="kk-match-hud__tier">{rightSubline}</span>
            )}
          </div>
          <HudCrest tier={rightTier} division={rightDivision} testId="hud-right-crest" />
        </div>
      </div>

      {/* Row 2 — HALF · DECK · TIMER BAR · TURN OWNER + REMAINING */}
      <div
        className="kk-match-hud__meta"
        data-testid={timerWrapperTestId || undefined}
      >
        <span className="kk-match-hud__meta__half" data-testid={halfTestId}>
          {(halfLabel || '1ST HALF').toString()}
        </span>
        <span className="kk-match-hud__meta__deck" data-testid={deckTestId}>
          DECK {deckCount ?? 0}
          {deckTotal ? `/${deckTotal}` : ''}
        </span>
        <span aria-hidden style={{ opacity: 0 }} /> {/* readability spacer, replaces the removed timer bar */}
        <span
          className={
            'kk-match-hud__meta__turn'
            + (timerTone === 'red' ? ' kk-match-hud__meta__turn--red' : '')
          }
          data-testid={timerTestId}
        >
          {turnOwner && <span style={{ color: 'rgba(245,243,236,0.72)', marginRight: 6 }}>{turnOwner} ·</span>}
          {timerLabel || '—'}
          {strikesLabel && (
            <span
              data-testid={strikesTestId}
              style={{ marginLeft: 8, color: '#F04444', fontSize: 11 }}
            >
              {strikesLabel}
            </span>
          )}
        </span>
      </div>
    </header>
  );
}
